// src/components/admin/AdminHeader.tsx
import React, { useEffect, useState } from 'react';
import { Box, IconButton, Avatar, Typography, Menu, MenuItem, Divider, ListItemIcon, Badge, List, ListItem, ListItemText } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface AdminHeaderProps {
  onToggleMenu: () => void;
}

// Normalize role for color coding (same as in SlideMenu)
const normalizeRole = (role: string): string => {
  const r = role?.toLowerCase().trim() || '';
  if (!r || r === 'new' || r === 'unknown') return 'admin';
  if (r.includes('super')) return 'super_admin';
  if (r === 'operation' || r === 'vendor') return r;
  return 'admin';
};

// Map normalized role to color — kept in sync with SlideMenu's role chip so
// the same role always shows the same color across the whole admin panel.
// Super Admin uses the actual VOTO brand orange-dark rather than a generic
// color, since it's the role shown by default in the admin experience.
const getRoleColor = (normalizedRole: string): string => {
  switch (normalizedRole) {
    case 'super_admin': return 'var(--orange-dark, #e94c1c)'; // Brand orange
    case 'admin':       return '#2196f3'; // Blue
    case 'operation':   return '#ff9800'; // Amber
    case 'vendor':      return '#4caf50'; // Green
    default:            return 'var(--orange, #F76338)'; // Brand orange (fallback)
  }
};

const AdminHeader: React.FC<AdminHeaderProps> = ({ onToggleMenu }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [lastNotificationCheck, setLastNotificationCheck] = useState<string>(() => new Date(Date.now() - 60 * 1000).toISOString());
  const [notificationUnread, setNotificationUnread] = useState(0);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  // --- Get user info from localStorage (keys set in adminLogin.tsx) ---
  const isVendor = (localStorage.getItem('user_role') || '').toLowerCase() === 'vendor';
  const userName  = (isVendor ? localStorage.getItem('user_name') : localStorage.getItem('admin_name')) || 'Admin';
  const userEmail = (isVendor ? localStorage.getItem('user_email') : localStorage.getItem('admin_email')) || 'admin@example.com';

  // Raw role from API (preserved under 'role')
  const rawRole = localStorage.getItem('role') || localStorage.getItem('user_role') || 'admin';

  // Normalized role for color coding
  const normalizedRole = normalizeRole(rawRole);

  // Avatar letter
  const avatarLetter = userName.charAt(0).toUpperCase() || userEmail.charAt(0).toUpperCase();

  // Format raw role for display (capitalize first letter)
  const displayRole = rawRole
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  useEffect(() => {
    let active = true;
    const loadNotifications = async () => {
      try {
        const token = isVendor ? localStorage.getItem('vendor_token') : localStorage.getItem('admin_token');
        if (!token) return;
        const path = isVendor ? '/admin/vendor-notifications?limit=50' : '/admin/admin-notifications?limit=50';
        const response = await fetch(`${process.env.REACT_APP_SERVER_ENDPOINT}${path}`, { headers: { Authorization: `Bearer ${token}`, authkey: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3' } });
        const data = await response.json();
        if (active && !data.error) { setNotifications(data.notifications || []); setNotificationUnread(Number(data.unread_count || 0)); }
      } catch (err) { console.warn('Notification polling failed', err); }
    };
    loadNotifications();
    const timer = window.setInterval(loadNotifications, 5000);
    return () => { active = false; window.clearInterval(timer); };
  }, [isVendor]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/adminProfile');
  };

  const handleSettings = () => {
    handleClose();
    navigate('/globalSettings');
  };

  const handleLogout = () => {
    const wasVendor = (localStorage.getItem('user_role') || '').toLowerCase() === 'vendor';

    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_name');
    localStorage.removeItem('admin_type');
    localStorage.removeItem('role');
    localStorage.removeItem('admin_role_id');
    localStorage.removeItem('admin_permissions');
    localStorage.removeItem('admin_perm_map');
    localStorage.removeItem('vendor_token');
    localStorage.removeItem('vendor_id');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_id');

    handleClose();
    navigate(wasVendor ? '/vendorlogin' : '/adminLogin');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '71px',
        px: 2,
        backgroundColor: 'var(--white, #FFFFFF)',
        borderBottom: '1px solid var(--border, #E8E8E8)',
        boxShadow: 'var(--shadow-sm, 0 2px 12px rgba(0,0,0,0.06))',
        fontFamily: 'var(--ff-body, "Montserrat", sans-serif)',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      {/* Left Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={onToggleMenu}
          sx={{
            display: { xs: 'flex', md: 'none' },
            color: 'var(--dark, #1A1A1A)',
            transition: 'background-color var(--transition, 0.28s cubic-bezier(0.4,0,0.2,1))',
            '&:hover': { backgroundColor: 'rgba(247,99,56,0.08)' },
          }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
            fontWeight: 700,
            color: 'var(--dark, #1A1A1A)',
            fontSize: '16px',
            letterSpacing: '-0.01em',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Admin Panel
        </Typography>
      </Box>

      {/* Right Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Role Badge - shows raw role name */}
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            backgroundColor: getRoleColor(normalizedRole),
            borderRadius: '20px',
            boxShadow: 'var(--shadow-sm, 0 2px 12px rgba(0,0,0,0.06))',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'white',
              fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
              fontWeight: 700,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {displayRole}
          </Typography>
        </Box>

        <>
            <IconButton
              onClick={async (e) => {
                setNotificationAnchor(e.currentTarget);

                // Opening the notification panel counts as reading the currently
                // unread notifications. Keep the badge strictly tied to the
                // backend unread_count — never fall back to total notifications.
                const unreadIds = notifications
                  .filter((n: any) => Number(n.is_read) !== 1)
                  .map((n: any) => Number(n.id))
                  .filter(Boolean);

                if (unreadIds.length === 0) return;

                const token = isVendor
                  ? localStorage.getItem('vendor_token')
                  : localStorage.getItem('admin_token');
                const readUrl = isVendor
                  ? '/admin/vendor-notifications/read'
                  : '/admin/admin-notifications/read';

                try {
                  const response = await fetch(
                    `${process.env.REACT_APP_SERVER_ENDPOINT}${readUrl}`,
                    {
                      method: 'POST',
                      headers: {
                        Authorization: `Bearer ${token}`,
                        authkey: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ notification_ids: unreadIds }),
                    }
                  );

                  if (response.ok) {
                    setNotificationUnread(0);
                    setNotifications((current) =>
                      current.map((n: any) =>
                        unreadIds.includes(Number(n.id)) ? { ...n, is_read: 1 } : n
                      )
                    );
                  }
                } catch (err) {
                  console.warn('Failed to mark notifications as read', err);
                }
              }}
              aria-label="Notifications"
            >
              <Badge
                badgeContent={notificationUnread > 0 ? notificationUnread : 0}
                color="error"
                max={99}
              >
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>
            <Menu
              anchorEl={notificationAnchor}
              open={Boolean(notificationAnchor)}
              onClose={() => setNotificationAnchor(null)}
              PaperProps={{ sx: { width: 360, maxHeight: 460, borderRadius: 2 } }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                <Typography fontWeight={700}>Notifications</Typography>
              </Box>
              <List dense>
                {notifications.length === 0 ? (
                  <ListItem><ListItemText primary="No new notifications." /></ListItem>
                ) : notifications.slice(0, 10).map((n:any) => (
                  <ListItem
                    key={n.id}
                    button
                    onClick={async () => {
                      setNotificationAnchor(null);

                      const token = isVendor
                        ? localStorage.getItem('vendor_token')
                        : localStorage.getItem('admin_token');
                      const readUrl = isVendor
                        ? '/admin/vendor-notifications/read'
                        : '/admin/admin-notifications/read';

                      try {
                        const response = await fetch(
                          `${process.env.REACT_APP_SERVER_ENDPOINT}${readUrl}`,
                          {
                            method: 'POST',
                            headers: {
                              Authorization: `Bearer ${token}`,
                              authkey: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ notification_ids: [n.id] }),
                          }
                        );

                        if (response.ok && Number(n.is_read) !== 1) {
                          setNotificationUnread((count) => Math.max(0, count - 1));
                          setNotifications((current) =>
                            current.map((item: any) =>
                              item.id === n.id ? { ...item, is_read: 1 } : item
                            )
                          );
                        }
                      } catch (err) {
                        console.warn('Failed to mark notification as read', err);
                      }

                      if (n.entity_type === 'product') navigate('/productManagement');
                      else if (n.entity_type === 'vendor') navigate('/vendorApplications');
                      else navigate('/adminOrderManagement');
                    }}
                  >
                    <ListItemText primary={n.title} secondary={n.message} />
                  </ListItem>
                ))}
              </List>
            </Menu>
          </>

        <Avatar
          onClick={handleClick}
          sx={{
            width: 32,
            height: 32,
            backgroundColor: getRoleColor(normalizedRole),
            fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
            fontSize: '14px',
            fontWeight: 700,
            ml: 1,
            cursor: 'pointer',
            boxShadow: 'var(--shadow-sm, 0 2px 12px rgba(0,0,0,0.06))',
            transition: 'opacity var(--transition, 0.28s cubic-bezier(0.4,0,0.2,1))',
            '&:hover': { opacity: 0.85 },
          }}
        >
          {avatarLetter}
        </Avatar>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              minWidth: 240,
              borderRadius: 'var(--radius, 14px)',
              boxShadow: 'var(--shadow-md, 0 8px 32px rgba(0,0,0,0.10))',
              fontFamily: 'var(--ff-body, "Montserrat", sans-serif)',
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
                fontFamily: 'var(--ff-body, "Montserrat", sans-serif)',
                fontSize: '14px',
                transition: 'background-color var(--transition, 0.28s cubic-bezier(0.4,0,0.2,1))',
                '&:hover': {
                  backgroundColor: 'rgba(247,99,56,0.06)',
                },
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Profile Info Section */}
          <Box sx={{ px: 2, py: 1.5, cursor: 'default' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
                fontWeight: 700, 
                color: 'var(--dark, #1A1A1A)',
                mb: 0.5,
              }}
            >
              {userName}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'var(--gray, #6B6B6B)',
                display: 'block',
                mb: 0.5,
              }}
            >
              {userEmail}
            </Typography>
            <Box
              sx={{
                display: 'inline-block',
                px: 1,
                py: 0.25,
                backgroundColor: getRoleColor(normalizedRole),
                borderRadius: '20px',
                mt: 0.5,
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'white',
                  fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
                  fontWeight: 700,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {displayRole}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1, borderColor: 'var(--border, #E8E8E8)' }} />

          {/* Menu Items */}
          <MenuItem onClick={handleProfile}>
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" sx={{ color: 'var(--dark, #1A1A1A)' }} />
            </ListItemIcon>
            Your Profile
          </MenuItem>

          <MenuItem onClick={handleSettings}>
            <ListItemIcon>
              <SettingsOutlinedIcon fontSize="small" sx={{ color: 'var(--dark, #1A1A1A)' }} />
            </ListItemIcon>
            Settings
          </MenuItem>

          <Divider sx={{ my: 1, borderColor: 'var(--border, #E8E8E8)' }} />

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: '#d72c0d' }} />
            </ListItemIcon>
            <Typography sx={{ color: '#d72c0d', fontWeight: 600, fontFamily: 'var(--ff-body, "Montserrat", sans-serif)' }}>
              Logout
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default AdminHeader;